import { useCallback, useState, useEffect } from 'react';
import izenuAPI from '../../services/izenuAPI';

const CDN = 'https://dpagz47n4g1lw.cloudfront.net';

function getArtworkUrl(artistId, artwork) {
  if (!artwork) return null;
  if (artwork.startsWith('http')) return artwork;
  return `${CDN}/albums/${artistId}/${artwork}`;
}

export default function NewAlbum() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create form state
  const [artistId, setArtistId] = useState('');
  const [artistName, setArtistName] = useState('');
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [priceUsd, setPriceUsd] = useState('');
  const [info, setInfo] = useState('');
  const [launchDate, setLaunchDate] = useState('');
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Artist search state
  const [searchStr, setSearchStr] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Edit state
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editInfo, setEditInfo] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editPriceUsd, setEditPriceUsd] = useState('');
  const [editLaunchDate, setEditLaunchDate] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const fetchAlbums = useCallback(async () => {
    try {
      const { data: r } = await izenuAPI.get('/admin/album/list.php');
      if (r.response) setAlbums(r.albums || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlbums(); }, []);

  // Search artists
  const searchArtist = useCallback(async () => {
    if (!searchStr.trim()) return;
    setSearching(true);
    try {
      const { data: r } = await izenuAPI.get('/admin/album/searchUser.php?artist=' + encodeURIComponent(searchStr));
      if (r.response) {
        setSearchResults(r.artists || []);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setSearching(false);
    }
  }, [searchStr]);

  const selectArtist = useCallback((artist) => {
    setArtistId(artist.user_id);
    setArtistName(artist.artist_name || artist.name);
    setSearchResults([]);
    setSearchStr('');
  }, []);

  // File upload handler
  const onFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['png', 'jpg', 'jpeg'].includes(ext)) {
      setMessage({ type: 'error', text: 'Tipo de ficheiro inválido. Use PNG ou JPG.' });
      return;
    }
    setArtworkFile(file);
    const reader = new FileReader();
    reader.onload = () => setArtworkPreview(reader.result);
    reader.readAsDataURL(file);
  }, []);

  // Save new album
  const saveAlbum = useCallback(async (e) => {
    e.preventDefault();
    if (!artistId) {
      setMessage({ type: 'error', text: 'Selecione um artista primeiro.' });
      return;
    }
    if (!title.trim()) {
      setMessage({ type: 'error', text: 'Preencha o título do álbum.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        artist_id: artistId,
        title: title.trim(),
        info: info.trim(),
        price: parseFloat(price) || 0,
        artwork: artworkFile ? artworkFile.name : '',
        launch_date: launchDate || new Date().toISOString().slice(0, 19).replace('T', ' ')
      };
      const { data: r } = await izenuAPI.post('/admin/album/insert.php', payload);
      if (r.response) {
        setMessage({ type: 'success', text: 'Álbum criado com sucesso!' });
        setTitle(''); setPrice(''); setPriceUsd(''); setInfo(''); setLaunchDate('');
        setArtistId(''); setArtistName(''); setArtworkFile(null); setArtworkPreview(null);
        fetchAlbums();
      } else {
        setMessage({ type: 'error', text: 'Erro ao criar álbum.' });
      }
    } catch (err) {
      console.log(err);
      setMessage({ type: 'error', text: 'Erro de rede ao criar álbum.' });
    } finally {
      setSaving(false);
    }
  }, [artistId, title, info, price, launchDate, artworkFile, fetchAlbums]);

  // Start editing
  const startEdit = useCallback((album) => {
    setEditId(album.id);
    setEditTitle(album.title);
    setEditInfo(album.info || '');
    setEditPrice(album.price);
    setEditPriceUsd(album.price_usd || '');
    setEditLaunchDate(album.launch_date ? album.launch_date.slice(0, 16).replace(' ', 'T') : '');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditId(null);
  }, []);

  // Save edit
  const saveEdit = useCallback(async () => {
    setEditSaving(true);
    try {
      const { data: r } = await izenuAPI.post('/admin/album/update.php', {
        album_id: editId,
        title: editTitle.trim(),
        info: editInfo.trim(),
        price: parseFloat(editPrice) || 0,
        price_usd: parseFloat(editPriceUsd) || null,
        launch_date: editLaunchDate ? editLaunchDate.replace('T', ' ') + ':00' : null
      });
      if (r.response) {
        setEditId(null);
        fetchAlbums();
      } else {
        alert('Erro ao atualizar álbum.');
      }
    } catch (err) {
      console.log(err);
      alert('Erro de rede.');
    } finally {
      setEditSaving(false);
    }
  }, [editId, editTitle, editInfo, editPrice, editPriceUsd, editLaunchDate, fetchAlbums]);

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <h1 style={s.pageTitle}>Álbuns à Venda (IAP)</h1>
        <p style={s.pageDesc}>Gestão de álbuns vendidos como compra in-app na App Store e loja web. Cada álbum contém faixas de áudio independentes do catálogo de streaming.</p>
      </div>

      {/* Albums List */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Álbuns Registados</h3>
        <p style={s.cardDesc}>Lista de todos os álbuns disponíveis para compra. Clique em "Editar" para alterar os dados de um álbum.</p>
        {loading ? (
          <p style={s.empty}>A carregar...</p>
        ) : albums.length === 0 ? (
          <p style={s.empty}>Nenhum álbum registado.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {albums.map((album) => (
              <div key={album.id} style={s.albumRow}>
                <img
                  src={album.artwork_front || ''}
                  alt=""
                  style={s.albumThumb}
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editId === album.id ? (
                    <div style={s.editForm}>
                      <div style={s.editRow}>
                        <label style={s.editLabel}>Título</label>
                        <input style={s.editInput} value={editTitle} onChange={e => setEditTitle(e.target.value)} />
                      </div>
                      <div style={s.editRow}>
                        <label style={s.editLabel}>Info</label>
                        <input style={s.editInput} value={editInfo} onChange={e => setEditInfo(e.target.value)} />
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div style={s.editRow}>
                          <label style={s.editLabel}>Preço Kz</label>
                          <input style={{ ...s.editInput, width: 100 }} type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                        </div>
                        <div style={s.editRow}>
                          <label style={s.editLabel}>Preço USD</label>
                          <input style={{ ...s.editInput, width: 100 }} type="number" step="0.01" value={editPriceUsd} onChange={e => setEditPriceUsd(e.target.value)} />
                        </div>
                        <div style={s.editRow}>
                          <label style={s.editLabel}>Lançamento</label>
                          <input style={{ ...s.editInput, width: 190 }} type="datetime-local" value={editLaunchDate} onChange={e => setEditLaunchDate(e.target.value)} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button style={s.btnSave} onClick={saveEdit} disabled={editSaving}>
                          {editSaving ? 'A guardar...' : 'Guardar'}
                        </button>
                        <button style={s.btnCancel} onClick={cancelEdit}>Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={s.albumTitle}>{album.title}</p>
                      <p style={s.albumSub}>
                        {album.artist_name} · {album.track_count} faixa{album.track_count !== 1 ? 's' : ''}
                        {album.price ? ` · ${Number(album.price).toLocaleString('pt-PT')} Kz` : ''}
                        {album.price_usd ? ` · $${album.price_usd} USD` : ''}
                      </p>
                      {album.info && <p style={s.albumInfo}>{album.info}</p>}
                      <p style={s.albumDate}>
                        Lançamento: {album.launch_date ? new Date(album.launch_date).toLocaleDateString('pt-PT') : '—'}
                        {album.apple_product_id ? ` · Apple: ${album.apple_product_id}` : ''}
                      </p>
                    </>
                  )}
                </div>
                {editId !== album.id && (
                  <button style={s.btnEdit} onClick={() => startEdit(album)}>Editar</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Album Form */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Adicionar Novo Álbum</h3>
        <p style={s.cardDesc}>Preencha os dados do álbum. Pesquise e selecione o artista, carregue a capa e defina o preço e data de lançamento.</p>

        {message && (
          <div style={{ ...s.message, backgroundColor: message.type === 'success' ? '#ecfdf5' : '#fef2f2', color: message.type === 'success' ? '#059669' : '#dc2626', borderColor: message.type === 'success' ? '#a7f3d0' : '#fecaca' }}>
            {message.text}
          </div>
        )}

        <form onSubmit={saveAlbum}>
          {/* Artist Search */}
          <div style={s.field}>
            <label style={s.label}>Artista</label>
            {artistId ? (
              <div style={s.selectedArtist}>
                <span>{artistName} (ID: {artistId})</span>
                <button type="button" style={s.btnRemoveArtist} onClick={() => { setArtistId(''); setArtistName(''); }}>✕</button>
              </div>
            ) : (
              <>
                <div style={s.searchRow}>
                  <input
                    style={s.input}
                    type="text"
                    placeholder="Pesquisar artista pelo nome..."
                    value={searchStr}
                    onChange={e => setSearchStr(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchArtist(); } }}
                  />
                  <button type="button" style={s.btnSearch} onClick={searchArtist} disabled={searching}>
                    {searching ? '...' : 'Pesquisar'}
                  </button>
                </div>
                {searchResults.length > 0 && (
                  <div style={s.searchResults}>
                    {searchResults.map((a, i) => (
                      <div key={i} style={s.searchItem} onClick={() => selectArtist(a)}>
                        {a.photo && <img src={a.photo} alt="" style={s.searchPhoto} onError={e => { e.target.style.display = 'none'; }} />}
                        <span>{a.artist_name || a.name} (ID: {a.user_id})</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Artwork */}
          <div style={s.field}>
            <label style={s.label}>Capa do Álbum</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {artworkPreview && <img src={artworkPreview} alt="preview" style={s.previewImg} />}
              <input type="file" accept=".png,.jpg,.jpeg" onChange={onFileChange} style={s.fileInput} />
            </div>
            <p style={s.hint}>Formatos aceites: PNG, JPG. Recomendado: 1000x1000px.</p>
          </div>

          {/* Title */}
          <div style={s.field}>
            <label style={s.label}>Título do Álbum</label>
            <input style={s.input} type="text" placeholder="Ex: Sessões de Roma" value={title} onChange={e => setTitle(e.target.value)} required />
          </div>

          {/* Prices */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ ...s.field, flex: '1 1 150px' }}>
              <label style={s.label}>Preço (Kz)</label>
              <input style={s.input} type="number" placeholder="700" value={price} onChange={e => setPrice(e.target.value)} />
            </div>
            <div style={{ ...s.field, flex: '1 1 150px' }}>
              <label style={s.label}>Preço (USD)</label>
              <input style={s.input} type="number" step="0.01" placeholder="0.99" value={priceUsd} onChange={e => setPriceUsd(e.target.value)} />
            </div>
          </div>

          {/* Info */}
          <div style={s.field}>
            <label style={s.label}>Informação Adicional</label>
            <textarea style={s.textarea} rows={3} placeholder="Descrição do álbum..." value={info} onChange={e => setInfo(e.target.value)} />
          </div>

          {/* Launch Date */}
          <div style={s.field}>
            <label style={s.label}>Data de Lançamento</label>
            <input style={s.input} type="datetime-local" value={launchDate} onChange={e => setLaunchDate(e.target.value)} />
          </div>

          <button type="submit" style={s.btnSubmit} disabled={saving}>
            {saving ? 'A criar...' : 'Criar Álbum'}
          </button>
        </form>
      </div>
    </div>
  );
}

const s = {
  container: { padding: '0 20px 40px 20px', maxWidth: 1000 },
  header: { marginBottom: 24 },
  pageTitle: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  pageDesc: { margin: '4px 0 0 0', fontSize: 13, color: '#999', fontWeight: 400, lineHeight: 1.5 },
  card: {
    padding: 24, backgroundColor: '#fff', borderRadius: 12,
    boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
    marginBottom: 20,
  },
  cardTitle: { fontSize: 16, fontWeight: 600, color: '#333', margin: '0 0 4px 0' },
  cardDesc: { margin: '0 0 16px 0', fontSize: 12, color: '#aaa', fontWeight: 400, lineHeight: 1.4 },
  empty: { color: '#ccc', textAlign: 'center', padding: 40, fontSize: 14 },

  // Album list
  albumRow: {
    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  albumThumb: {
    width: 56, height: 56, borderRadius: 8, objectFit: 'cover',
    backgroundColor: '#f0f0f0', flexShrink: 0,
  },
  albumTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: '#222' },
  albumSub: { margin: '2px 0 0 0', fontSize: 12, color: '#888' },
  albumInfo: { margin: '4px 0 0 0', fontSize: 12, color: '#aaa', fontStyle: 'italic' },
  albumDate: { margin: '4px 0 0 0', fontSize: 11, color: '#bbb' },

  // Edit inline
  editForm: { display: 'flex', flexDirection: 'column', gap: 6 },
  editRow: { display: 'flex', alignItems: 'center', gap: 8 },
  editLabel: { fontSize: 12, color: '#888', width: 60, flexShrink: 0 },
  editInput: {
    flex: 1, padding: '6px 10px', fontSize: 13, border: '1px solid #ddd',
    borderRadius: 6, outline: 'none',
  },
  btnEdit: {
    padding: '6px 14px', fontSize: 12, fontWeight: 500, color: '#6366f1',
    backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6,
    cursor: 'pointer', flexShrink: 0, alignSelf: 'flex-start',
  },
  btnSave: {
    padding: '6px 14px', fontSize: 12, fontWeight: 500, color: '#fff',
    backgroundColor: '#6366f1', border: 'none', borderRadius: 6, cursor: 'pointer',
  },
  btnCancel: {
    padding: '6px 14px', fontSize: 12, fontWeight: 500, color: '#666',
    backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer',
  },

  // Create form
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#444', marginBottom: 6 },
  input: {
    width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #ddd',
    borderRadius: 8, outline: 'none', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #ddd',
    borderRadius: 8, outline: 'none', boxSizing: 'border-box', resize: 'vertical',
  },
  fileInput: { fontSize: 13 },
  hint: { margin: '4px 0 0 0', fontSize: 11, color: '#bbb' },
  previewImg: { width: 64, height: 64, borderRadius: 8, objectFit: 'cover' },

  // Artist search
  searchRow: { display: 'flex', gap: 8 },
  btnSearch: {
    padding: '10px 18px', fontSize: 13, fontWeight: 500, color: '#fff',
    backgroundColor: '#6366f1', border: 'none', borderRadius: 8, cursor: 'pointer',
    flexShrink: 0,
  },
  searchResults: {
    marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8,
    backgroundColor: '#fafafa', maxHeight: 200, overflowY: 'auto',
  },
  searchItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: 13,
  },
  searchPhoto: { width: 28, height: 28, borderRadius: 14, objectFit: 'cover' },
  selectedArtist: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', backgroundColor: '#eef2ff', borderRadius: 8,
    fontSize: 14, color: '#4338ca',
  },
  btnRemoveArtist: {
    background: 'none', border: 'none', fontSize: 16, color: '#6366f1',
    cursor: 'pointer', padding: '0 4px',
  },

  // Message
  message: {
    padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
    marginBottom: 16, border: '1px solid',
  },

  // Submit
  btnSubmit: {
    padding: '12px 28px', fontSize: 14, fontWeight: 600, color: '#fff',
    backgroundColor: '#6366f1', border: 'none', borderRadius: 8, cursor: 'pointer',
    marginTop: 8,
  },
};
